import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { feature as topoFeature } from "topojson-client";

/* ============================================================================
   DATA
   ============================================================================ */

const DEFAULT_DATA = [
  { iso: "DEU", name: "Germany", coordinates: [10.4515, 51.1657], clients: 22, type: "operating", label: "Europe", color: "#10B981" },
  { iso: "HUN", name: "Hungary", coordinates: [19.5033, 47.1625], clients: 12, type: "operating", label: "Europe", color: "#10B981" },
  { iso: "IND", name: "India", coordinates: [78.9629, 20.5937], clients: 28, type: "operating", label: "India", color: "#10B981" },
  { iso: "SGP", name: "Singapore", coordinates: [103.8198, 1.3521], clients: 5, type: "hub", label: "Asia", color: "#3B82F6" },
];

const LOCAL_GEOJSON_URL = "/data/world.geojson";
const REMOTE_GEOJSON_URL =
  "https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json";

/* ============================================================================
   GLOBE SETTINGS
   ============================================================================ */

const GLOBE_RADIUS = 200;
const DOT_STEP = 1.25;
const DOT_RADIUS_OFFSET = 1.7;
const BORDER_RADIUS_OFFSET = 2.3;
const MARKER_RADIUS_OFFSET = 7;

/* ============================================================================
   CAMERA FRAMING  (this is what makes it look like your reference image)

   For this project's latLonToVector3(), a longitude L faces the camera when:
       rotation.y = -(L in radians) - Math.PI / 2

   Europe (10E) -> India (79E) -> Singapore (104E) is centered near 58E, so:
       rotation.y = -2.58

   BASE_TILT tips the north pole toward the camera so Germany/Hungary (~50N)
   show at the upper-left while Singapore (1N) stays at the right edge.
   ============================================================================ */

// Globe rocks left->right, then right->left, between START_ANGLE and END_ANGLE.
// Window where Germany, India and Singapore are ALL visible:
//   START_ANGLE = centre at ~80E (Singapore near middle-right)
//   END_ANGLE   = centre at ~36E (Europe near middle-left)
// The globe sweeps START -> END (left to right), then END -> START
// (right to left), back and forth continuously with no reset or fade.
const START_ANGLE = -2.95; // centre ~79E
const END_ANGLE = -2.65;   // centre ~62E
// Germany and Singapore are ~92 degrees apart. Their midpoint is ~35N, 71E,
// so the view must be tilted ~0.6 rad and centred near 71E to see BOTH at once.
const BASE_TILT = 0.6;
const ROTATION_SPEED = 0.0005; // raise for faster
const FADE_SPEED = 0.025;
const CAMERA_Z = 730;

/* ============================================================================
   HELPERS
   ============================================================================ */

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function latLonToVector3(lat, lon, radius = GLOBE_RADIUS) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);

  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
}

function getCountryName(feature) {
  const p = feature?.properties || {};
  return p.name || p.NAME || p.ADMIN || p.NAME_EN || p.NAME_LONG || "";
}

function getCountryISO(feature) {
  const p = feature?.properties || {};
  const id = String(
    feature?.id || p.ISO_A3 || p.iso_a3 || p.ADM0_A3 || ""
  ).toUpperCase();
  const name = String(getCountryName(feature)).toLowerCase();

  if (id === "IND" || name === "india") return "IND";
  if (id === "DEU" || name === "germany") return "DEU";
  if (id === "HUN" || name === "hungary") return "HUN";
  if (id === "SGP" || name === "singapore") return "SGP";
  return id;
}

function pointInRing(lon, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersects =
      yi > lat !== yj > lat &&
      lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointInPolygon(lon, lat, polygon) {
  if (!polygon?.length) return false;
  if (!pointInRing(lon, lat, polygon[0])) return false;
  for (let i = 1; i < polygon.length; i++) {
    if (pointInRing(lon, lat, polygon[i])) return false;
  }
  return true;
}

function pointInFeature(lon, lat, feature) {
  const geometry = feature?.geometry;
  if (!geometry) return false;

  if (geometry.type === "Polygon") {
    return pointInPolygon(lon, lat, geometry.coordinates);
  }

  if (geometry.type === "MultiPolygon") {
    for (const polygon of geometry.coordinates) {
      if (pointInPolygon(lon, lat, polygon)) return true;
    }
  }
  return false;
}

function getFeatureBounds(feature) {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  function collect(coords) {
    if (typeof coords?.[0] === "number") {
      minLon = Math.min(minLon, coords[0]);
      maxLon = Math.max(maxLon, coords[0]);
      minLat = Math.min(minLat, coords[1]);
      maxLat = Math.max(maxLat, coords[1]);
      return;
    }
    if (Array.isArray(coords)) coords.forEach(collect);
  }

  collect(feature?.geometry?.coordinates);

  if (minLon === Infinity) {
    return { minLon: -180, maxLon: 180, minLat: -90, maxLat: 90 };
  }
  return { minLon, maxLon, minLat, maxLat };
}

function findCountryAtPoint(lon, lat, features) {
  for (const item of features) {
    const b = item.bounds;
    if (lon >= b.minLon && lon <= b.maxLon && lat >= b.minLat && lat <= b.maxLat) {
      if (pointInFeature(lon, lat, item.feature)) return item.feature;
    }
  }
  return null;
}

function buildBorderPositions(features, radius) {
  const positions = [];

  function addRing(ring) {
    if (!ring || ring.length < 2) return;
    for (let i = 0; i < ring.length - 1; i++) {
      const [lon1, lat1] = ring[i];
      const [lon2, lat2] = ring[i + 1];
      if (Math.abs(lon2 - lon1) > 180) continue;

      const v1 = latLonToVector3(lat1, lon1, radius);
      const v2 = latLonToVector3(lat2, lon2, radius);
      positions.push(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z);
    }
  }

  features.forEach((feature) => {
    const g = feature.geometry;
    if (!g) return;
    if (g.type === "Polygon") g.coordinates.forEach(addRing);
    if (g.type === "MultiPolygon") {
      g.coordinates.forEach((polygon) => polygon.forEach(addRing));
    }
  });

  return new Float32Array(positions);
}

function createGlowTexture(color) {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, `${color}FF`);
  g.addColorStop(0.18, `${color}CC`);
  g.addColorStop(0.42, `${color}55`);
  g.addColorStop(1, `${color}00`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function createRingTexture(color) {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, 128, 128);
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(64, 64, 42, 0, Math.PI * 2);
  ctx.stroke();
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

async function loadGeoJSON() {
  /*
   * 1. Bundled map (npm: world-atlas + topojson-client).
   *    The data is shipped inside your build, so it works in production
   *    with no network request, no CORS and no CSP problems.
   */
  try {
    const mod = await import("world-atlas/countries-50m.json");
    const topo = mod.default || mod;
    const collection = topoFeature(topo, topo.objects.countries);
    if (collection && Array.isArray(collection.features) && collection.features.length) {
      return collection;
    }
  } catch (err) {
    console.warn("Bundled world map failed, trying other sources...", err);
  }

  /* 2. Local file (public/data/world.geojson) */
  try {
    const response = await fetch(LOCAL_GEOJSON_URL);
    const type = response.headers.get("content-type") || "";
    // On many hosts a missing file returns index.html with status 200,
    // so make sure we really got JSON.
    if (response.ok && !type.includes("text/html")) {
      const local = await response.json();
      if (local && Array.isArray(local.features)) return local;
    }
  } catch (err) {
    console.warn("Local GeoJSON not available.");
  }

  /* 3. Remote fallback */
  try {
    const response = await fetch(REMOTE_GEOJSON_URL);
    if (!response.ok) throw new Error(`Remote GeoJSON HTTP ${response.status}`);
    const remote = await response.json();
    if (remote && Array.isArray(remote.features)) return remote;
  } catch (err) {
    console.error("Remote GeoJSON failed:", err);
  }

  return null;
}

/* ============================================================================
   COMPONENT
   ============================================================================ */

export default function GlobalOperationsImpactGlobe({ data = DEFAULT_DATA }) {
  const containerRef = useRef(null);
  const labelRefs = useRef({});
  const tooltipRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const resolvedData = useMemo(
    () => data.map((item) => ({ ...item, name: item.name || item.label || "Location" })),
    [data]
  );

  const labelGroups = useMemo(() => {
    const groups = {};
    resolvedData.forEach((item) => {
      const key = item.label || item.name;
      if (!groups[key]) groups[key] = { key, members: [] };
      groups[key].members.push(item);
    });

    return Object.values(groups).map((group) => {
      const n = group.members.length;
      const lat = group.members.reduce((t, i) => t + i.coordinates[1], 0) / n;
      const lon = group.members.reduce((t, i) => t + i.coordinates[0], 0) / n;
      const clients = group.members.reduce((t, i) => t + (i.clients || 0), 0);
      return { ...group, lat, lon, clients };
    });
  }, [resolvedData]);

  useEffect(() => {
    let mounted = true;
    let renderer = null;
    let animationId = null;
    let resizeObserver = null;
    let resumeTimer = null;

    const disposables = [];

    async function init() {
      const container = containerRef.current;
      if (!container) return;

      setLoading(true);
      setError(false);

      const geojson = await loadGeoJSON();
      if (!mounted) return;

      if (!geojson || !Array.isArray(geojson.features)) {
        setLoading(false);
        setError(true);
        return;
      }

      const countryFeatures = geojson.features
        .filter((f) => getCountryName(f).toLowerCase() !== "antarctica")
        .map((feature) => ({ feature, bounds: getFeatureBounds(feature) }));

      /* ---------------- scene / camera / renderer ---------------- */

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 2000);
      camera.position.z = CAMERA_Z;

      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.15;

      container.innerHTML = "";
      container.appendChild(renderer.domElement);

      // Make the canvas always fill the circle exactly, so the globe is centred
      // (without this, browser zoom / high-DPI screens push it down and to the right).
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
      renderer.domElement.style.display = "block";

      scene.add(new THREE.AmbientLight(0xffffff, 0.45));
      const light = new THREE.DirectionalLight(0xffffff, 1.2);
      light.position.set(-250, 280, 400);
      scene.add(light);

      /* ---------------- globe group ---------------- */

      const globeGroup = new THREE.Group();
      globeGroup.rotation.y = START_ANGLE;
      globeGroup.rotation.x = BASE_TILT;
      scene.add(globeGroup);

      /* ocean */
      const oceanGeometry = new THREE.SphereGeometry(GLOBE_RADIUS, 96, 96);
      const oceanMaterial = new THREE.MeshStandardMaterial({
        color: 0x080d14,
        roughness: 0.92,
        metalness: 0.02,
      });
      const ocean = new THREE.Mesh(oceanGeometry, oceanMaterial);
      globeGroup.add(ocean);
      disposables.push(oceanGeometry, oceanMaterial);

      /* rim */
      const rimGeometry = new THREE.SphereGeometry(GLOBE_RADIUS * 1.015, 96, 96);
      const rimMaterial = new THREE.MeshBasicMaterial({
        color: 0x182230,
        transparent: true,
        opacity: 0.75,
        side: THREE.BackSide,
      });
      globeGroup.add(new THREE.Mesh(rimGeometry, rimMaterial));
      disposables.push(rimGeometry, rimMaterial);

      /* land dots */
      const dotPositions = [];
      for (let lat = -58; lat <= 84; lat += DOT_STEP) {
        const cosLat = Math.max(Math.cos((lat * Math.PI) / 180), 0.32);
        const lonStep = DOT_STEP / cosLat;

        for (let lon = -180; lon < 180; lon += lonStep) {
          if (!findCountryAtPoint(lon, lat, countryFeatures)) continue;

          const noise = Math.sin(lon * 12.9898 + lat * 78.233) * 0.5 + 0.5;
          if (noise < 0.055) continue;

          const p = latLonToVector3(lat, lon, GLOBE_RADIUS + DOT_RADIUS_OFFSET);
          dotPositions.push(p.x, p.y, p.z);
        }
      }

      const dotsGeometry = new THREE.BufferGeometry();
      dotsGeometry.setAttribute("position", new THREE.Float32BufferAttribute(dotPositions, 3));
      const dotsMaterial = new THREE.PointsMaterial({
        color: 0xd6dee8,
        size: 2.5,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.92,
        depthWrite: false,
      });
      globeGroup.add(new THREE.Points(dotsGeometry, dotsMaterial));
      disposables.push(dotsGeometry, dotsMaterial);

      /* borders */
      const borderGeometry = new THREE.BufferGeometry();
      borderGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(
          buildBorderPositions(
            countryFeatures.map((i) => i.feature),
            GLOBE_RADIUS + BORDER_RADIUS_OFFSET
          ),
          3
        )
      );
      const borderMaterial = new THREE.LineBasicMaterial({
        color: 0xa9b6c6,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
      });
      globeGroup.add(new THREE.LineSegments(borderGeometry, borderMaterial));
      disposables.push(borderGeometry, borderMaterial);

      /* highlight border */
      const highlightGeometry = new THREE.BufferGeometry();
      const highlightMaterial = new THREE.LineBasicMaterial({
        color: 0x10b981,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
      });
      const highlightBorder = new THREE.LineSegments(highlightGeometry, highlightMaterial);
      highlightBorder.visible = false;
      globeGroup.add(highlightBorder);
      disposables.push(highlightGeometry, highlightMaterial);

      /* ---------------- markers ---------------- */

      const markers = [];
      const pulses = [];
      const arcs = [];

      resolvedData.forEach((location, index) => {
        const isIndia = location.iso === "IND";
        const position = latLonToVector3(
          location.coordinates[1],
          location.coordinates[0],
          GLOBE_RADIUS + MARKER_RADIUS_OFFSET
        );
        const color = location.color || "#10B981";

        const coreGeometry = new THREE.SphereGeometry(isIndia ? 5 : 4, 20, 20);
        const coreMaterial = new THREE.MeshBasicMaterial({ color });
        const core = new THREE.Mesh(coreGeometry, coreMaterial);
        core.position.copy(position);
        globeGroup.add(core);

        const glowTexture = createGlowTexture(color);
        const glowMaterial = new THREE.SpriteMaterial({
          map: glowTexture,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        const glow = new THREE.Sprite(glowMaterial);
        const glowSize = isIndia ? 48 : 38;
        glow.scale.set(glowSize, glowSize, 1);
        glow.position.copy(position);
        globeGroup.add(glow);

        const ringTexture = createRingTexture(color);
        const ringMaterial = new THREE.SpriteMaterial({
          map: ringTexture,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        const ring = new THREE.Sprite(ringMaterial);
        ring.position.copy(position);
        ring.scale.set(18, 18, 1);
        globeGroup.add(ring);

        markers.push({ location, glow, glowSize });
        pulses.push({ ring, baseSize: isIndia ? 23 : 19, offset: index * 0.5 });

        disposables.push(
          coreGeometry,
          coreMaterial,
          glowTexture,
          glowMaterial,
          ringTexture,
          ringMaterial
        );
      });

      /* ---------------- arcs from India ---------------- */

      const india = resolvedData.find((i) => i.iso === "IND");
      if (india) {
        const indiaPosition = latLonToVector3(
          india.coordinates[1],
          india.coordinates[0],
          GLOBE_RADIUS + MARKER_RADIUS_OFFSET
        );

        labelGroups
          .filter((g) => g.key !== "India")
          .forEach((group, index) => {
            const target = latLonToVector3(
              group.lat,
              group.lon,
              GLOBE_RADIUS + MARKER_RADIUS_OFFSET
            );
            const distance = indiaPosition.angleTo(target);
            const midpoint = indiaPosition
              .clone()
              .add(target)
              .normalize()
              .multiplyScalar(GLOBE_RADIUS + 18 + distance * 42);

            const curve = new THREE.QuadraticBezierCurve3(indiaPosition, midpoint, target);
            const geometry = new THREE.BufferGeometry().setFromPoints(curve.getPoints(70));
            const material = new THREE.LineBasicMaterial({
              color: 0xd7e4f2,
              transparent: true,
              opacity: 0.28,
              depthWrite: false,
            });
            globeGroup.add(new THREE.Line(geometry, material));

            const pulseGeometry = new THREE.SphereGeometry(2, 12, 12);
            const pulseMaterial = new THREE.MeshBasicMaterial({
              color: 0xe4edf7,
              transparent: true,
              opacity: 0.9,
              blending: THREE.AdditiveBlending,
            });
            const pulse = new THREE.Mesh(pulseGeometry, pulseMaterial);
            globeGroup.add(pulse);

            arcs.push({ curve, pulse, offset: index * 0.32 });
            disposables.push(geometry, material, pulseGeometry, pulseMaterial);
          });
      }

      /* ---------------- resize ---------------- */

      function resize() {
        const width = container.clientWidth;
        const height = container.clientHeight || width;
        if (!width) return;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height, false);
      }

      resize();
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(container);

      /* ---------------- interaction ---------------- */

      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();

      let dragging = false;
      let autoRotation = true;
      let rotationX = 0;
      let angle = START_ANGLE;
      let fade = 1;
      let phase = "run";
      let direction = 1; // 1 = left -> right, -1 = right -> left
      let lastX = 0;
      let lastY = 0;

      const canvas = renderer.domElement;
      canvas.style.cursor = "grab";
      canvas.style.touchAction = "none";

      function clearHighlight() {
        highlightBorder.visible = false;
        if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
      }

      function highlightCountry(country, x, y) {
        const positions = buildBorderPositions(
          [country],
          GLOBE_RADIUS + BORDER_RADIUS_OFFSET + 1
        );
        highlightGeometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        highlightGeometry.computeBoundingSphere();
        highlightBorder.visible = true;

        const countryName = getCountryName(country);
        const iso = getCountryISO(country);
        const matching = resolvedData.find((item) => item.iso === iso);

        if (tooltipRef.current) {
          tooltipRef.current.textContent = matching?.clients
            ? `${countryName} · ${matching.clients} clients`
            : countryName;
          tooltipRef.current.style.left = `${x}px`;
          tooltipRef.current.style.top = `${y}px`;
          tooltipRef.current.style.opacity = "1";
        }
      }

      function handlePointerDown(event) {
        dragging = true;
        autoRotation = false;
        lastX = event.clientX;
        lastY = event.clientY;
        canvas.style.cursor = "grabbing";
        clearTimeout(resumeTimer);
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch (_) {}
      }

      function handlePointerMove(event) {
        const rect = canvas.getBoundingClientRect();

        if (dragging) {
          angle += (event.clientX - lastX) * 0.003;
          angle = clamp(angle, START_ANGLE - 0.35, END_ANGLE + 0.35);
          phase = "run";
          fade = 1;
          canvas.style.opacity = "1";
          rotationX += (event.clientY - lastY) * 0.002;
          rotationX = clamp(rotationX, -0.38, 0.38);
          lastX = event.clientX;
          lastY = event.clientY;
          return;
        }

        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -(((event.clientY - rect.top) / rect.height) * 2 - 1);
        raycaster.setFromCamera(mouse, camera);

        const hits = raycaster.intersectObject(ocean);
        if (!hits.length) {
          clearHighlight();
          return;
        }

        const local = globeGroup.worldToLocal(hits[0].point.clone());
        const radius = local.length();
        const lat = 90 - Math.acos(clamp(local.y / radius, -1, 1)) * (180 / Math.PI);

        let theta = Math.atan2(local.z, -local.x);
        if (theta < 0) theta += Math.PI * 2;
        const lon = theta * (180 / Math.PI) - 180;

        const country = findCountryAtPoint(lon, lat, countryFeatures);
        if (!country) {
          clearHighlight();
          return;
        }

        highlightCountry(country, event.clientX - rect.left, event.clientY - rect.top);
      }

      function handlePointerUp() {
        dragging = false;
        canvas.style.cursor = "grab";
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(() => {
          if (mounted) autoRotation = true;
        }, 1500);
      }

      function handlePointerLeave() {
        if (!dragging) clearHighlight();
      }

      canvas.addEventListener("pointerdown", handlePointerDown);
      canvas.addEventListener("pointermove", handlePointerMove);
      canvas.addEventListener("pointerup", handlePointerUp);
      canvas.addEventListener("pointercancel", handlePointerUp);
      canvas.addEventListener("pointerleave", handlePointerLeave);

      /* ---------------- labels ---------------- */

      function updateLabels() {
        const rect = canvas.getBoundingClientRect();

        labelGroups.forEach((group) => {
          const element = labelRefs.current[group.key];
          if (!element) return;

          const world = latLonToVector3(group.lat, group.lon, GLOBE_RADIUS + 12).applyMatrix4(
            globeGroup.matrixWorld
          );

          const normal = world.clone().normalize();
          const toCamera = camera.position.clone().sub(world).normalize();

          if (normal.dot(toCamera) < 0.05) {
            element.style.opacity = "0";
            return;
          }

          const projected = world.clone().project(camera);
          element.style.left = `${(projected.x * 0.5 + 0.5) * rect.width}px`;
          element.style.top = `${(-projected.y * 0.5 + 0.5) * rect.height}px`;
          element.style.opacity = String(fade);
        });
      }

      /* ---------------- animation ---------------- */

      const clock = new THREE.Clock();

      function animate() {
        if (!mounted) return;
        animationId = requestAnimationFrame(animate);

        const elapsed = clock.getElapsedTime();

        if (autoRotation && !dragging) {
          if (angle < START_ANGLE - 0.001) {
            angle += 0.012; // after a drag, glide back into the range
            direction = 1;
          } else if (angle > END_ANGLE + 0.001) {
            angle -= 0.012;
            direction = -1;
          } else {
            // slows down smoothly near both ends, fastest in the middle
            const p = (angle - START_ANGLE) / (END_ANGLE - START_ANGLE);
            const ease = 0.3 + 0.7 * Math.sin(Math.PI * clamp(p, 0, 1));
            angle += ROTATION_SPEED * ease * direction;

            if (angle >= END_ANGLE) direction = -1; // reached right end -> go back
            if (angle <= START_ANGLE) direction = 1; // reached left end -> go forward
          }
        }

        globeGroup.rotation.y = angle;
        globeGroup.rotation.x = BASE_TILT + rotationX;

        pulses.forEach((item) => {
          const progress = (elapsed * 0.38 + item.offset) % 1;
          const size = item.baseSize * (0.55 + progress * 1.9);
          item.ring.scale.set(size, size, 1);
          item.ring.material.opacity = (1 - progress) * 0.4;
        });

        arcs.forEach((item) => {
          const progress = (elapsed * 0.12 + item.offset) % 1;
          item.pulse.position.copy(item.curve.getPoint(progress));
        });

        markers.forEach((marker, index) => {
          const s = marker.glowSize * (1 + Math.sin(elapsed * 2 + index) * 0.08);
          marker.glow.scale.set(s, s, 1);
        });

        renderer.render(scene, camera);
        updateLabels();
      }

      animate();
      setLoading(false);
    }

    init();

    return () => {
      mounted = false;
      clearTimeout(resumeTimer);
      if (animationId) cancelAnimationFrame(animationId);
      if (resizeObserver) resizeObserver.disconnect();

      disposables.forEach((r) => {
        if (r && typeof r.dispose === "function") r.dispose();
      });

      if (renderer) {
        renderer.dispose();
        renderer.domElement?.remove();
      }
    };
  }, [resolvedData, labelGroups]);

  /* ==========================================================================
     UI
     ========================================================================== */

  return (
    <section style={{ width: "100%", background: "#F8F7F4", overflow: "hidden" }}>
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "70px 24px 85px" }}>
        {/* Heading */}
        <div style={{ maxWidth: "760px", margin: "0 auto 45px", textAlign: "center" }}>
          <div
            style={{
              color: "#10B981",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: "12px",
            }}
          >
            Global Operations
          </div>

          <h2
            style={{
              margin: 0,
              color: "#101828",
              fontSize: "clamp(32px, 4vw, 52px)",
              lineHeight: 1.05,
              fontWeight: 750,
              letterSpacing: "-0.04em",
            }}
          >
            Global Operations &amp; Client Distribution
          </h2>

          <p
            style={{
              maxWidth: "680px",
              margin: "18px auto 0",
              color: "#667085",
              fontSize: "15px",
              lineHeight: 1.7,
            }}
          >
            Our global presence connects teams, technology and opportunities across
            different regions.
          </p>
        </div>

        {/* Content */}
        <div
          className="global-globe-layout"
          style={{
            display: "grid",
            gridTemplateColumns: "0.8fr 1.2fr",
            alignItems: "center",
            gap: "30px",
          }}
        >
          {/* Left copy */}
          <div className="global-globe-copy">
            <div
              style={{
                color: "#10B981",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              Our Global Presence
            </div>

            <h3
              style={{
                margin: "15px 0 0",
                color: "#101828",
                fontSize: "clamp(38px, 5vw, 65px)",
                lineHeight: 0.96,
                letterSpacing: "-0.05em",
                fontWeight: 750,
              }}
            >
              Think global,
              <br />
              Work together,
              <br />
              Customer focused by design.
            </h3>

            <p
              style={{
                maxWidth: "430px",
                margin: "25px 0 0",
                color: "#667085",
                fontSize: "15px",
                lineHeight: 1.75,
              }}
            >
              With a presence across global markets, we work alongside organizations to
              understand their challenges, strengthen collaboration, and create solutions
              built for real-world impact.
            </p>
          </div>

          {/* Globe */}
          <div
            className="global-globe-wrapper"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "590px",
              aspectRatio: "1 / 1",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: "6%",
                borderRadius: "50%",
                background: "rgba(0,0,0,.28)",
                filter: "blur(35px)",
                opacity: 0.4,
              }}
            />

            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: "#03060A",
                boxShadow: "0 25px 70px rgba(0,0,0,.22)",
              }}
            />

            <div
              ref={containerRef}
              style={{
                position: "absolute",
                inset: "4.5%",
                borderRadius: "50%",
                overflow: "hidden",
                background:
                  "radial-gradient(circle at 42% 34%, #121923 0%, #080D14 52%, #03060A 100%)",
              }}
            />

            <div
              style={{
                pointerEvents: "none",
                position: "absolute",
                inset: "5.5%",
                borderRadius: "50%",
                border: "8px solid rgba(28,38,52,.65)",
              }}
            />

            <div
              style={{
                pointerEvents: "none",
                position: "absolute",
                inset: "3.7%",
                borderRadius: "50%",
                border: "1px solid rgba(160,174,192,.15)",
              }}
            />

            {/* Labels + tooltip */}
            <div style={{ pointerEvents: "none", position: "absolute", inset: "4.5%" }}>
              {labelGroups.map((group) => (
                <div
                  key={group.key}
                  ref={(element) => {
                    if (element) labelRefs.current[group.key] = element;
                  }}
                  style={{
                    position: "absolute",
                    left: "0px",
                    top: "0px",
                    transform: "translate(-50%, -100%)",
                    minWidth: "98px",
                    padding: "10px 13px",
                    borderRadius: "12px",
                    background: "rgba(255,255,255,.97)",
                    border: "1px solid rgba(16,24,40,.08)",
                    boxShadow: "0 8px 22px rgba(16,24,40,.18)",
                    opacity: 0,
                    transition: "opacity .2s ease",
                  }}
                >
                  <div style={{ color: "#101828", fontSize: "13px", fontWeight: 750, lineHeight: 1.1 }}>
                    {group.key}
                  </div>
                  <div style={{ marginTop: "5px", color: "#667085", fontSize: "11px", lineHeight: 1.2 }}>
                    {group.members.map((item) => item.name).join(" & ")}
                  </div>
                </div>
              ))}

              <div
                ref={tooltipRef}
                style={{
                  position: "absolute",
                  pointerEvents: "none",
                  left: "0px",
                  top: "0px",
                  transform: "translate(-50%, calc(-100% - 15px))",
                  padding: "7px 10px",
                  borderRadius: "8px",
                  background: "rgba(15,23,42,.95)",
                  color: "#FFFFFF",
                  fontSize: "11px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  opacity: 0,
                  transition: "opacity .15s ease",
                }}
              />
            </div>

            {loading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    width: "34px",
                    height: "34px",
                    border: "2px solid rgba(255,255,255,.16)",
                    borderTopColor: "#FFFFFF",
                    borderRadius: "50%",
                    animation: "globalGlobeSpin .8s linear infinite",
                  }}
                />
              </div>
            )}

            {error && (
              <div
                style={{
                  position: "absolute",
                  inset: "10%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "#94A3B8",
                  fontSize: "12px",
                  lineHeight: 1.6,
                }}
              >
                World map could not be loaded.
              </div>
            )}

            <div
              style={{
                position: "absolute",
                bottom: "-38px",
                left: 0,
                right: 0,
                textAlign: "center",
                color: "#98A2B3",
                fontSize: "11px",
              }}
            >
              Drag to rotate · Hover countries to explore
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes globalGlobeSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 900px) {
          .global-globe-layout {
            grid-template-columns: 1fr !important;
            gap: 50px !important;
          }
          .global-globe-copy { text-align: center; }
          .global-globe-copy p {
            margin-left: auto !important;
            margin-right: auto !important;
          }
        }

        @media (max-width: 600px) {
          .global-globe-wrapper { max-width: 470px !important; }
        }
      `}</style>
    </section>
  );
}